import React, { useState, useEffect } from 'react';
import { XMarkIcon, TrashIcon, PlusIcon } from '../../../../components/Icons';
import { SCHEDULE_REGEX } from './OperationConstants';

interface ScheduleRange {
    days: string[];
    start: string;
    end: string;
}

interface ScheduleRangeSelectorModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialValue: string;
    onSave: (value: string) => void;
}

const DAYS_ORDER = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];
const DAY_ALIAS_MAP: Record<string, string> = {
    lu: 'Lu', lun: 'Lu', ma: 'Ma', mar: 'Ma', mi: 'Mi', mie: 'Mi', mié: 'Mi',
    ju: 'Ju', jue: 'Ju', vi: 'Vi', vie: 'Vi', sa: 'Sá', sab: 'Sá', sá: 'Sá', sáb: 'Sá', do: 'Do', dom: 'Do'
};

function parseStringToRanges(str: string): ScheduleRange[] {
    if (!str || !str.trim()) return [];
    const ranges: ScheduleRange[] = [];
    const segments = str.split(';');

    for (const segment of segments) {
        const trimmed = segment.trim();
        if (!trimmed) continue;
        const parts = trimmed.split(/\s+/);
        if (parts.length < 2) continue;

        const daysPart = parts[0];
        const timesPart = parts.slice(1).join(' ');

        const days: string[] = [];
        const dayTokens = daysPart.split(',');
        for (const token of dayTokens) {
            const subToken = token.trim();
            if (!subToken) continue;
            if (subToken.includes('-')) {
                const [start, end] = subToken.split('-').map(s => s.trim().toLowerCase());
                const startDay = DAY_ALIAS_MAP[start];
                const endDay = DAY_ALIAS_MAP[end];
                if (startDay && endDay) {
                    const startIndex = DAYS_ORDER.indexOf(startDay);
                    const endIndex = DAYS_ORDER.indexOf(endDay);
                    if (startIndex !== -1 && endIndex !== -1) {
                        const min = Math.min(startIndex, endIndex);
                        const max = Math.max(startIndex, endIndex);
                        for (let i = min; i <= max; i++) {
                            if (!days.includes(DAYS_ORDER[i])) days.push(DAYS_ORDER[i]);
                        }
                    }
                }
            } else {
                const mapped = DAY_ALIAS_MAP[subToken.toLowerCase()];
                if (mapped && !days.includes(mapped)) days.push(mapped);
            }
        }
        days.sort((a, b) => DAYS_ORDER.indexOf(a) - DAYS_ORDER.indexOf(b));

        const timeIntervals = timesPart.split(',');
        for (const interval of timeIntervals) {
            const match = interval.trim().match(/^([01]\d|2[0-3]):[0-5]\d-([01]\d|2[0-3]):[0-5]\d$/);
            if (match && days.length > 0) {
                const [start, end] = interval.trim().split('-');
                ranges.push({ days: [...days], start, end });
            }
        }
    }
    return ranges;
}

function formatDays(daysArray: string[]): string {
    if (daysArray.length === 0) return '';
    const sorted = [...daysArray].sort((a, b) => DAYS_ORDER.indexOf(a) - DAYS_ORDER.indexOf(b));
    let isContiguous = true;
    for (let i = 1; i < sorted.length; i++) {
        const prevIdx = DAYS_ORDER.indexOf(sorted[i - 1]);
        const currIdx = DAYS_ORDER.indexOf(sorted[i]);
        if (currIdx !== prevIdx + 1) { isContiguous = false; break; }
    }
    return (isContiguous && sorted.length > 1) ? `${sorted[0]}-${sorted[sorted.length - 1]}` : sorted.join(',');
}

function formatRangesToString(ranges: ScheduleRange[]): string {
    const groups: Record<string, string[]> = {};
    ranges.forEach(r => {
        const dStr = formatDays(r.days);
        if (!dStr) return;
        if (!groups[dStr]) groups[dStr] = [];
        const timeStr = `${r.start}-${r.end}`;
        if (!groups[dStr].includes(timeStr)) groups[dStr].push(timeStr);
    });
    return Object.entries(groups).map(([daysStr, times]) => `${daysStr} ${times.join(',')}`).join('; ');
}

export const ScheduleRangeSelectorModal: React.FC<ScheduleRangeSelectorModalProps> = ({
    isOpen, onClose, initialValue, onSave
}) => {
    const [ranges, setRanges] = useState<ScheduleRange[]>([]);
    const [selectedDays, setSelectedDays] = useState<string[]>([]);
    const [startTime, setStartTime] = useState('08:00');
    const [endTime, setEndTime] = useState('17:00');

    useEffect(() => {
        if (isOpen) {
            setRanges(parseStringToRanges(initialValue));
            setSelectedDays([]);
            setStartTime('08:00');
            setEndTime('17:00');
        }
    }, [isOpen, initialValue]);

    if (!isOpen) return null;

    const toggleDay = (day: string) => {
        setSelectedDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
    };

    const handleQuickPreset = (type: 'lu-vi' | 'sa-do' | 'todos') => {
        if (type === 'lu-vi') setSelectedDays(['Lu', 'Ma', 'Mi', 'Ju', 'Vi']);
        else if (type === 'sa-do') setSelectedDays(['Sá', 'Do']);
        else setSelectedDays([...DAYS_ORDER]);
    };

    const addRange = () => {
        if (selectedDays.length === 0) return alert('Por favor selecciona al menos un día');
        if (startTime >= endTime) return alert('La hora de inicio debe ser anterior a la hora de fin');
        
        const newRange: ScheduleRange = {
            days: [...selectedDays].sort((a, b) => DAYS_ORDER.indexOf(a) - DAYS_ORDER.indexOf(b)),
            start: startTime,
            end: endTime
        };
        setRanges(prev => [...prev, newRange]);
        setSelectedDays([]);
    };

    const removeRange = (idx: number) => {
        setRanges(prev => prev.filter((_, i) => i !== idx));
    };

    const handleSaveClick = () => {
        const formatted = formatRangesToString(ranges);
        if (formatted && !SCHEDULE_REGEX.test(formatted)) {
            return alert('Error de consistencia en el formato. Por favor revise los rangos.');
        }
        onSave(formatted);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-sky-50/50">
                    <h3 className="text-sm font-black text-[#00234F] uppercase tracking-wider">Configurar Horario de Visita</h3>
                    <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"><XMarkIcon className="w-5 h-5 text-gray-500" /></button>
                </div>
                <div className="p-6 overflow-y-auto space-y-6 flex-grow custom-scrollbar">
                    <div className="space-y-3">
                        <label className="block text-[11px] font-black text-[#00234F] uppercase tracking-wider">Paso 1: Seleccionar Días</label>
                        <div className="flex flex-wrap gap-2">
                            {DAYS_ORDER.map(day => {
                                const isSelected = selectedDays.includes(day);
                                return (
                                    <button key={day} onClick={() => toggleDay(day)} className={`px-3 py-2 rounded-xl text-xs font-bold transition-all border ${isSelected ? 'bg-sky-600 border-sky-600 text-white shadow-md' : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-350'}`}>{day}</button>
                                );
                            })}
                        </div>
                        <div className="flex gap-2 text-[10px] font-black text-sky-600 uppercase tracking-wider">
                            <button onClick={() => handleQuickPreset('lu-vi')} className="hover:underline">Lu-Vi</button>
                            <span>|</span>
                            <button onClick={() => handleQuickPreset('sa-do')} className="hover:underline">Fines de Semana</button>
                            <span>|</span>
                            <button onClick={() => handleQuickPreset('todos')} className="hover:underline">Todos</button>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[11px] font-black text-[#00234F] uppercase tracking-wider mb-2">Hora Inicio</label>
                            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="w-full text-xs font-bold border border-gray-200 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-sky-500 bg-gray-55" />
                        </div>
                        <div>
                            <label className="block text-[11px] font-black text-[#00234F] uppercase tracking-wider mb-2">Hora Fin</label>
                            <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="w-full text-xs font-bold border border-gray-200 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-sky-500 bg-gray-55" />
                        </div>
                    </div>
                    <button onClick={addRange} className="w-full py-2.5 bg-sky-50 hover:bg-sky-100 text-sky-700 rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all border border-sky-100"><PlusIcon className="w-4 h-4" /> Agregar Rango</button>
                    <div className="space-y-3 pt-4 border-t border-gray-100">
                        <label className="block text-[11px] font-black text-[#00234F] uppercase tracking-wider">Horarios Definidos</label>
                        {ranges.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">No tienes ningún rango de horario configurado.</p>
                        ) : (
                            <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                                {ranges.map((range, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-700 animate-fade-in">
                                        <span>{formatDays(range.days)} <span className="text-gray-400 font-normal px-1">|</span> {range.start} - {range.end}</span>
                                        <button onClick={() => removeRange(index)} className="p-1 hover:bg-red-50 text-red-500 rounded-lg transition-colors"><TrashIcon className="w-4 h-4" /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="p-6 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50/50">
                    <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-gray-500 hover:bg-gray-100 rounded-xl text-xs font-bold transition-colors">Cancelar</button>
                    <button onClick={handleSaveClick} className="px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-md transition-colors">Guardar Horario</button>
                </div>
            </div>
        </div>
    );
};
